/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { AppBar, Tabs, Tab, Box } from "@mui/material";
import { WarningRounded } from "@mui/icons-material";
import { FakeText, Card } from "@totalsoft/rocket-ui";
import PartnerIdentificationContainer from "./identification/IdentificationContainer";
import ContactInfoContainer from "./contactInfos/ContactInfoContainer";
import PartnerPropertyContainer from "./properties/PropertyContainer";
import ConsentContainer from "./consents/ConsentContainer";
import { getInt } from "../../../utils/functions";
import {
  loadPartner,
  loadPartnerTypes,
  loadCounties,
  loadCities,
  loadClientByIdCard,
  getPartnerNationalityId,
  loadPersonDocumentTypes
} from "../actionCreators";
import {
  getPartnerTypes,
  getPartner,
  getBanks,
  getCurrencies,
  getBankAccountTypes,
  getPersonDocumentTypes
} from "../selectors";
import memoize from "lodash.memoize";
import BankAccountContainer from "./bankaccounts/BankAccountContainer";
import HeaderPortal from "components/layout/HeaderPortal";
import ShareholderContainer from "./shareholders/ShareholderContainer";
import AddressesContainer from "./addresses/AddressesContainer";
import PersonWorkplacesContainer from "./personWorkplaces/PersonWorkplacesContainer";
import PartnerExtraInformationContainer from "./extraInfo/PartnerExtraInformationContainer";
import ActivityFieldsContainer from "./activities/ActivityFieldsContainer";
import MessagesContainer from "./messages/MessagesContainer";
import LogoContainer from "./logo/LogoContainer";
import ScoringContainer from "./scoring/ScoringContainer";
import { NoResultsCard } from "components/common/CardStyles";
import BalanceSheetContainer from "./balanceSheet/BalanceSheetContainer";
import { withParams } from "../../../providers/ParamsConsumer";
import { RightsContext } from "../../../providers/RightsContext";
import {
  shape,
  required,
  maxLength,
  when,
  fromModel,
  Validator,
  Failure,
  Success,
  ValidationError,
  stopOnFirstFailure
} from "@totalsoft/pure-validations";
import {
  dateIsLessThanOrEqualToCurrentDate,
  dateIsGreaterThanGivenDate,
  validateDistanceBetweenTwoDates,
  validatePersonUniqueId,
  validateCompanyUniqueId
} from "../../../utils/pure-validation/primitiveValidators";
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import { useDirtyInfo } from "@totalsoft/change-tracking-react";
import DealerContainer from "./dealers/DealerContainer";
import PartnerHistoryLogContainer from "./log/PartnerLogContainer";
import { hasAuthorization } from "../../../utils/functions";
import * as rightsCodes from "constants/rightsCodes";
import useLocator from "hooks/useLocator";
import { checkBirthDateAccordingToUniqueId, validateSeriesAndNumber } from "utils/simple-validations";
import withRouter from "components/__temporary/hoc/withRouter";
import { Prompt } from "react-router-dom";
import EmployeesContainer from "./employees/EmployeesContainer";
import { useReactiveVar } from "@apollo/client";
import { partnerTypeIdVar, synchronizationInProgressVar } from "../../../variables";
import { useQueryWithSimpleErrorHandling } from 'hooks/apolloWrappers';
import { PARTNER_SYNCHRONIZATION_STATUS_QUERY } from "../queries";
import { useSubscription } from "@apollo/client";
import { SYNCHRONIZATION_STATUS_CHANGED } from "../subscriptions";
import { useMutation, useApolloClient } from "@apollo/client";
import { RETRY_PARTNER_SYNCHRONIZATION } from "../../partnerSynchronization/mutations";
import { useDispatch } from "react-redux";
import { showInfo, showError } from "actions/actionCreators/snackbarActions";

export const validator = (
  isIndividual,
  isCompany,
  isBank,
  isSelfPartner,
  t,
  validityDaysForIdentityCard,
  keysFinNextIdPrefix,
  personDocumentType,
  defaultCountryCode
) => {
  const cnp = Validator(function cnp(content) {
    return validatePersonUniqueId(content, defaultCountryCode)
      ? Success
      : Failure(ValidationError(t("Validations.Generic.CNP")));
  });

  const cif = Validator(function cif(content) {
    return validateCompanyUniqueId(content, defaultCountryCode)
      ? Success
      : Failure(ValidationError(t("Validations.Generic.CIF")));
  });

  const cifOrCnp = Validator(function cifOrCnp(content) {
    return validateCompanyUniqueId(content, defaultCountryCode) || validatePersonUniqueId(content, defaultCountryCode)
      ? Success
      : Failure(ValidationError(t("Validations.Generic.Identifier")));
  });

  const isSeriesAndNumber = Validator(function isSeriesAndNumber(content) {
    return validateSeriesAndNumber(content, defaultCountryCode)
      ? Success
      : Failure(ValidationError(t("Validations.Client.SeriesAndNumber")));
  });

  const isPassport = content => {
    return content === personDocumentType?.find(x => x.personDocumentTypeCode === "PAS")?.personDocumentTypeId;
  };

  const birthDateAccordingToUniqueId = (cnp, defaultCountryCode) =>
    Validator(function birthDateAccordingToUniqueId(content) {
      return content
        ? checkBirthDateAccordingToUniqueId(cnp, content, defaultCountryCode)
          ? Success
          : Failure(ValidationError(t("Validations.Generic.BirthDate")))
        : Success;
    });
  return fromModel(item =>
    shape({
      partnerTypeId: required |> when(!isBank && !isSelfPartner),
      firstName: [required |> when(isIndividual === true), maxLength(150)] |> stopOnFirstFailure,
      lastName: [required |> when(isIndividual === true), maxLength(150)] |> stopOnFirstFailure,
      uniqueId:
        [
          required,
          cnp
            |> when(
              isIndividual === true &&
                item.uniqueId != null &&
                !item.isExternal &&
                !item.uniqueId.toUpperCase().startsWith(keysFinNextIdPrefix)
            ),
          cif
            |> when(
              isCompany === true &&
                item.uniqueId != null &&
                !item.isExternal &&
                !item.uniqueId.toUpperCase().startsWith(keysFinNextIdPrefix)
            ),
          cifOrCnp
            |> when(
              isCompany === false &&
                isIndividual === false &&
                item.uniqueId != null &&
                !item.isExternal &&
                !item.uniqueId.toUpperCase().startsWith(keysFinNextIdPrefix)
            )
        ] |> stopOnFirstFailure,
      partnerName: [required |> when(isIndividual === false), maxLength(200)] |> stopOnFirstFailure,
      nationalityId: required |> when(isIndividual === true),
      birthDate:
        birthDateAccordingToUniqueId(item.uniqueId, defaultCountryCode)
        |> when(isIndividual === true && !item.isExternal && validatePersonUniqueId(item.uniqueId, defaultCountryCode)),
      idSeriesAndNumber:
        isSeriesAndNumber |> when(Boolean(item.idSeriesAndNumber) && !isPassport(item.personDocumentTypeId)),
      identityCardReleaseDate: dateIsLessThanOrEqualToCurrentDate |> when(isIndividual === true),
      identityCardExpireDate:
        [
          dateIsGreaterThanGivenDate(item.identityCardReleaseDate, t("Partner.IdentityCardReleaseDate"))
            |> when(isIndividual === true),
          validateDistanceBetweenTwoDates(
            item.identityCardReleaseDate,
            t("Partner.IdentityCardReleaseDate"),
            t("Partner.IdentityCardExpireDate"),
            validityDaysForIdentityCard
          ) |> when(isIndividual === true)
        ] |> stopOnFirstFailure
    })
  );
};

const Container = ({
  partner,
  partnerLoaded,
  partnerTypes,
  actions,
  partnerLoading,
  personDocumentTypes,
  history,
  routeParams,
  ...props
}) => {
  const { t } = useTranslation();

  const client = useApolloClient();
  const dispatch = useDispatch();

  const partnerId = getInt(routeParams.partnerId);
  const [value, setValue] = useState("generalData");
  const [localPartner, setLocalPartner] = useState(partner || {});
  const [isWaitingForOCR, setIsWaitingForOCR] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [dirtyInfo, setDirtyInfo, resetDirtyInfo] = useDirtyInfo();
  const [isDirty, setIsDirty] = useState(false);
  const rights = useContext(RightsContext);
  const synchronizationInProgress = useReactiveVar(synchronizationInProgressVar);

  useQueryWithSimpleErrorHandling(PARTNER_SYNCHRONIZATION_STATUS_QUERY, {
    variables: { partnerId },
    fetchPolicy: "network-only",
    skip: !partnerId,
    onCompleted: data => {
      synchronizationInProgressVar(data.synchronizationStatus);
    }
  });

  useSubscription(SYNCHRONIZATION_STATUS_CHANGED, {
    variables: { partnerId },
    onData: ({ data: { data } }) => {
      synchronizationInProgressVar(data.synchronizationStatusChanged.synchronizationInProgress);
    }
  });

  const { isBank, isSelfPartner, path } = useLocator();

  const viewDealerContainer = hasAuthorization(rightsCodes.MASTER_PARTNER_DEALERS, rights);
  const viewPartnerLogContainer = hasAuthorization(rightsCodes.MASTER_PARTNER_HISTORY, rights);
  const viewScoringContainer = hasAuthorization(rightsCodes.MASTER_PARTNER_SCORING, rights);
  const viewBalanceSheetContainer = hasAuthorization(rightsCodes.MASTER_PARTNER_SCORING, rights);
  const viewShareholderPhone = hasAuthorization(rightsCodes.MASTER_PARTNER_SHAREHOLDER_PHONE, rights);
  const viewWarningArea = hasAuthorization(rightsCodes.MASTER_PARTNER_WARNING_AREA, rights);
  const personWorkplacesRead = hasAuthorization(rightsCodes.CLIENT_PERSONWORKPLACES_READ, rights);
  const personWorkplacesWrite = hasAuthorization(rightsCodes.CLIENT_PERSONWORKPLACES_WRITE, rights);
  const hasWritePermission = hasAuthorization(
    isBank
      ? rightsCodes.MASTER_PARTNER_BANKS_WRITE
      : isSelfPartner
      ? rightsCodes.MASTER_PARTNER_SELFPARTNER_WRITE
      : rightsCodes.MASTER_PARTNER_WRITE,
    rights
  );

  const isCompany = useMemo(() => {
    return (isSelfPartner || localPartner.partnerTypeId & props.params.basePJTypeId) === props.params.basePJTypeId;
  });

  const isIndividual = useMemo(() => {
    return (localPartner.partnerTypeId & props.params.basePFTypeId) === props.params.basePFTypeId;
  });
  const partnerExtraInfoParam = props.params.documentPartnerExtraInfo;

  const validatorPartner = useMemo(
    () =>
      validator(
        isIndividual,
        isCompany,
        isBank,
        isSelfPartner,
        t,
        props.params.validityDaysForIdentityCard,
        props.params.keysFinNextIdPrefix,
        personDocumentTypes,
        props.params.defaultCountryCode
      ),
    [
      isIndividual,
      isCompany,
      props.params.validityDaysForIdentityCard,
      props.params.keysFinNextIdPrefix,
      props.params.defaultCountryCode,
      personDocumentTypes
    ]
  );
  const [validationPartner, validatePartner, resetValidationPartner] = useDirtyFieldValidation(
    validatorPartner,
    dirtyInfo
  );

  const showWarningArea = useMemo(() => {
    return partnerId !== null && viewWarningArea;
  }, [partnerId, viewWarningArea]);

  const showScoringTab = useMemo(() => {
    return !isBank && !isSelfPartner && partnerId !== null && viewScoringContainer;
  }, [isBank, isSelfPartner, partnerId, viewScoringContainer]);

  const showDealerCard = useMemo(() => {
    return !isBank && !isSelfPartner && viewDealerContainer;
  }, [isBank, isSelfPartner, viewDealerContainer]);

  const partnerTypeId = useMemo(() => {
    if (isBank) return props.params.bankTypeId;
    return props.params.defaultPartnerTypeId;
  }, [isBank]);

  const queryPartnerTypeId = useMemo(() => {
    if (isBank) return props.params.bankTypeId;
    if (isSelfPartner) return props.params.selfPartnerTypeId;
    return null;
  });

  useEffect(() => {
    partnerTypeIdVar(queryPartnerTypeId);
    return () => {
      partnerTypeIdVar(null);
      synchronizationInProgressVar(null);
    };
  }, [queryPartnerTypeId]);

  useEffect(() => {
    if (partnerId !== null && queryPartnerTypeId !== undefined) actions.loadPartner(partnerId, queryPartnerTypeId);
  }, [partnerId, queryPartnerTypeId]);

  useEffect(() => {
    actions.loadPartnerTypes();
    actions.loadPersonDocumentTypes();
    if (partnerId && queryPartnerTypeId !== undefined) {
      actions.loadPartner(partnerId, queryPartnerTypeId);
    } else {
      setLocalPartner({
        partnerTypeId: partnerTypeId
      });
    }
  }, [queryPartnerTypeId]);

  useEffect(() => {
    if (partnerLoaded) setLocalPartner({ ...partner });
    else if (partnerId !== null && queryPartnerTypeId !== undefined) {
      actions.loadPartner(partnerId, queryPartnerTypeId);
    }
  }, [partnerLoaded, queryPartnerTypeId]);

  useEffect(() => {
    if (localPartner.individual != null) {
      setLocalPartner({
        ...localPartner,
        partnerName: `${localPartner.individual.lastName || ""} ${localPartner.individual.firstName || ""}`
      });
    }
  }, [
    localPartner.individual && localPartner.individual.firstName,
    localPartner.individual && localPartner.individual.lastName
  ]);

  useEffect(() => {
    if (partnerId === null || partnerId === undefined) {
      let partner = { partnerTypeId: partnerTypeId };

      if ((props.params.defaultPartnerTypeId & props.params.basePFTypeId) === props.params.basePFTypeId) {
        partner = {
          ...partner,
          individual: {
            nationalityId: props.params.defaultCitizenshipId,
            citizenshipId: props.params.defaultCitizenshipId,
            genderId: ""
          },
          uniqueId: ""
        };
      }
      setLocalPartner(partner);
    }
  }, [props.params.defaultPartnerTypeId, props.params.defaultCitizenshipId]);

  useEffect(() => {
    if (
      !localPartner.isExternal &&
      localPartner.uniqueId &&
      (localPartner.partnerTypeId & props.params.basePFTypeId) === props.params.basePFTypeId
    ) {
      getNationalityByUniqueId(localPartner.uniqueId).then(nationalityId => {
        if (nationalityId) {
          handleNationalityComputation(nationalityId);
        }
      });
    }
  }, [localPartner.uniqueId]);

  const handleChange = (_, newValue) => {
    setValue(newValue);
  };

  const onInternalPropertyChange = (property, prop, value) => {
    setLocalPartner(localPartner => ({
      ...localPartner,
      [property]: { ...localPartner[property], [prop]: value }
    }));
  };

  const handleSexComputation = value => onInternalPropertyChange("individual", "genderId", value);
  const handleNationalityComputation = value => {
    onInternalPropertyChange("individual", "nationalityId", value.nationalityId);
    onInternalPropertyChange("individual", "citizenshipId", value.nationalityId);
  };

  const onLocalPartnerPropertyChange = (prop1, prop2) => (value1, value2) => {
    if (!hasWritePermission) return;
    if (prop1 !== "partnerTypeId" || !partner?.chPartnerId) {
      setDirtyInfo(`${prop1}`);
      setLocalPartner({
        ...localPartner,
        [prop1]: value1,
        ...(prop2 && { [prop2]: value2 })
      });
    }

    setSaveDisabled(false);
    setIsDirty(true);
    if (prop1 === "partnerTypeId" && partner?.chPartnerId) {
      setLocalPartner({
        [prop1]: value1
      });
      resetValidationPartner();
    }
  };

  const getNationalityByUniqueId = async uniqueId => {
    let partner = { ...localPartner };
    if (!validatePersonUniqueId(uniqueId, props.params.defaultCountryCode)) return "";
    if (!!partner && !!partner.individual && !!partner.individual.nationalityId) return "";
    return await actions.getPartnerNationalityId(uniqueId, !!partner.isExternal);
  };

  const onPropertyChange = memoize(property => prop => value => {
    if (!hasWritePermission) return;
    setDirtyInfo(`${prop}`);
    setLocalPartner({
      ...localPartner,
      [property]: Object.assign(
        { ...localPartner[property] },
        {
          [prop]: value
        }
      )
    });
    setSaveDisabled(false);
    setIsDirty(true);
  });

  const onRedirect = id => {
    history.push(`/${path}/${id}`);
  };

  const handleUpload = memoize(async file => {
    if (!hasWritePermission) return;
    setIsWaitingForOCR(true);
    const ocrData = await actions.loadClientByIdCard(file, partnerId, () => setIsWaitingForOCR(false));
    if (ocrData && ocrData.generalData) {
      setLocalPartner(prevState => {
        return {
          ...prevState,
          uniqueId: ocrData.generalData.uniqueId,
          partnerCode: ocrData.generalData.uniqueId,
          partnerName: ocrData.generalData.partnerName,
          individual: {
            ...prevState.individual,
            firstName: ocrData.generalData.firstName,
            lastName: ocrData.generalData.lastName,
            idSeriesAndNumber: ocrData.generalData.idSeries + ocrData.generalData.idNumber,
            idIssuedBy: ocrData.generalData.idIssuedBy,
            identityCardReleaseDate: ocrData.generalData.identityCardReleaseDate,
            identityCardExpireDate: ocrData.generalData.identityCardExpireDate,
            birthDate: ocrData.generalData.birthDate,
            citizenshipId: ocrData.generalData.citizenshipId,
            nationalityId: ocrData.generalData.nationalityId,
            genderId: ocrData.generalData.genderId,
            personDocumentTypeId: ocrData.generalData.personDocumentTypeId
          }
        };
      });
      setSaveDisabled(false);
      setIsWaitingForOCR(false);
      setIsDirty(true);
      setDirtyInfo("firstName");
      setDirtyInfo("lastName");
      setDirtyInfo("idSeriesAndNumber");
      setDirtyInfo("idIssuedBy");
      setDirtyInfo("identityCardReleaseDate");
      setDirtyInfo("identityCardExpireDate");
      setDirtyInfo("birthDate");
      setDirtyInfo("uniqueId");
      setDirtyInfo("partnerName");
      setDirtyInfo("partnerCode");
    }
  });

  const onFileSelected = event => {
    if (event.target.files.length > 0) {
      let file = event.target.files[0];
      handleUpload(file);
    }
  };

  // #region Retry Partner Synchronization

  const [retryPartnerSynchronization] = useMutation(RETRY_PARTNER_SYNCHRONIZATION, {
    onError: error => {
      dispatch(showError(error?.message));
    },
    onCompleted: async data => {
      if (data.retryPartnerSynchronization.synchronizationCompleted)
        dispatch(showInfo(t("PartnerSynchronization.FinishSynchronizationRetry")));
      client.cache.evict({ id: "ROOT_QUERY", fieldName: "partnerSynchronizationQueue" });
    }
  });

  const handleRetrySynchronization = useCallback(() => {
    retryPartnerSynchronization({
      variables: { partnerId }
    });
  }, [partnerId, retryPartnerSynchronization]);

  // #endregion

  if (localPartner.partnerId !== partnerId && partnerId) return <NoResultsCard>{t("ResultsNotFound")}</NoResultsCard>;

  return partnerLoading ? (
    <Card sx={{ my: 1 }}>
      <FakeText lines={10} />
    </Card>
  ) : (
    partnerLoaded && (synchronizationInProgress != null || !partnerId) && (
      <Box position="relative">
        {synchronizationInProgress === true && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgcolor="rgba(255, 255, 255, 0.5)"
            zIndex={1}
            onClick={e => e.stopPropagation()}
            display="flex"
            justifyContent="center"
            alignItems="center"
          />
        )}
        <Prompt when={isDirty} message={() => t("LeavingWithoutSaving")} />
        <HeaderPortal
          partner={localPartner}
          inEdit={true}
          customTitle={
            synchronizationInProgress ? (
              <Box component="span" display="flex" alignItems="center" gap={0.5}>
                <WarningRounded color="warning" fontSize="small" />
                {t("Tooltips.PartnerSynchronizationInProgress")}
              </Box>
            ) : null
          }
        />
        <AppBar position="static" color="default" sx={{ my: 1, borderRadius: "1rem", px: 2 }}>
          <Tabs value={value} onChange={handleChange} indicatorColor="primary" textColor="primary" variant="scrollable">
            <Tab label={t("Tabs.GeneralData")} value="generalData" />
            {showScoringTab && <Tab label={t("Tabs.Scoring")} value="scoring" />}
            {partnerId !== null && isCompany && !isBank && !isSelfPartner && viewBalanceSheetContainer && (
              <Tab label={t("Tabs.BalanceSheet")} value="balanceSheet" />
            )}
            {partnerId !== null && viewPartnerLogContainer && <Tab label={t("Tabs.HistoryLog")} value="historyLog" />}
          </Tabs>
        </AppBar>
        {showWarningArea && (
          <MessagesContainer partnerId={partnerId} onRetrySynchronization={handleRetrySynchronization} />
        )}
        {value === "generalData" && (
          <PartnerIdentificationContainer
            partner={localPartner}
            defaultPartnerTypeId={props.params.defaultPartnerTypeId}
            partnerTypes={
              partner && !isIndividual
                ? partnerTypes?.filter(x => (x.partnerTypeId & props.params.basePFTypeId) !== props.params.basePFTypeId)
                : partnerTypes
            }
            onPropertyChange={onPropertyChange}
            onPartnerPropertyChange={onLocalPartnerPropertyChange}
            onRedirect={onRedirect}
            saveDisabled={saveDisabled}
            setSaveDisabled={setSaveDisabled}
            onSexComputation={handleSexComputation}
            validationPartner={validationPartner}
            validatePartner={validatePartner}
            dirtyInfo={dirtyInfo}
            setIsDirty={setIsDirty}
            partnerLoaded={partnerLoaded}
            onFileSelected={onFileSelected}
            hasWritePermission={hasWritePermission && !isWaitingForOCR}
            isWaitingForOCR={isWaitingForOCR}
            resetDirtyInfo={resetDirtyInfo}
            personDocumentTypes={personDocumentTypes}
            isIndividual={isIndividual}
            hidePartnerTypes={isBank || isSelfPartner}
          />
        )}
        {partnerId !== null && value === "generalData" && (
          <>
            <AddressesContainer
              partnerId={partnerId}
              isIndividual={localPartner && isIndividual}
              setIsDirty={setIsDirty}
              hasWritePermission={hasWritePermission && !isWaitingForOCR}
            />
            {localPartner && isIndividual && personWorkplacesRead && (
              <PersonWorkplacesContainer
                partnerId={partnerId}
                hasWritePermission={hasWritePermission && !isWaitingForOCR && personWorkplacesWrite}
              />
            )}
            <ContactInfoContainer
              partnerId={partnerId}
              contactModeParams={{
                contactModePhone: props.params.contactModePhone,
                contactModeEmail: props.params.contactModeEmail,
                contactModeMobilePhone: props.params.contactModeMobilePhone
              }}
              setIsDirty={setIsDirty}
              hasWritePermission={hasWritePermission && !isWaitingForOCR}
            />
            {localPartner && isSelfPartner && (
              <EmployeesContainer partnerId={partnerId} hasWritePermission={hasWritePermission} />
            )}
            {localPartner && !isIndividual && (
              <ShareholderContainer
                partnerId={partnerId}
                setIsDirty={setIsDirty}
                hasWritePermission={hasWritePermission && !isWaitingForOCR}
                viewShareholderPhone={viewShareholderPhone}
              />
            )}
            {localPartner && !isIndividual && (
              <ActivityFieldsContainer
                partnerId={partnerId}
                actions={actions}
                setIsDirty={setIsDirty}
                hasWritePermission={hasWritePermission && !isWaitingForOCR}
              />
            )}

            <BankAccountContainer
              partnerId={partnerId}
              actions={actions}
              setIsDirty={setIsDirty}
              hasWritePermission={hasWritePermission && !isWaitingForOCR}
            />
            <PartnerPropertyContainer
              actions={actions}
              partnerId={partnerId}
              setIsDirty={setIsDirty}
              hasWritePermission={hasWritePermission && !isWaitingForOCR}
            />
            {partnerExtraInfoParam && (
              <PartnerExtraInformationContainer
                partnerId={partnerId}
                hasWritePermission={hasWritePermission && !isWaitingForOCR}
              />
            )}
            {localPartner && isIndividual && (
              <ConsentContainer
                partnerId={partnerId}
                actions={actions}
                setIsDirty={setIsDirty}
                hasWritePermission={hasWritePermission && !isWaitingForOCR}
              />
            )}
            {localPartner && !isIndividual && props.params?.features?.isPartnerLogoEnabled && (
              <LogoContainer
                partnerId={partnerId}
                partner={localPartner}
                partnerLoaded={partnerLoaded}
                setIsDirty={setIsDirty}
                hasWritePermission={hasWritePermission && !isWaitingForOCR}
              ></LogoContainer>
            )}
            {showDealerCard && <DealerContainer actions={actions} partnerId={partnerId} setIsDirty={setIsDirty} />}
          </>
        )}
        {showScoringTab && value === "scoring" && <ScoringContainer partnerId={partnerId} actions={actions} />}
        {partnerId !== null && isCompany && value === "balanceSheet" && <BalanceSheetContainer partnerId={partnerId} />}
        {partnerId !== null && value === "historyLog" && <PartnerHistoryLogContainer />}
      </Box>
    )
  );
};

Container.propTypes = {
  partner: PropTypes.object,
  partnerLoaded: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
  partnerTypes: PropTypes.array.isRequired,
  currencies: PropTypes.array.isRequired,
  bankAccountTypes: PropTypes.array.isRequired,
  bankList: PropTypes.array.isRequired,
  params: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool,
  personDocumentTypes: PropTypes.array.isRequired,
  history: PropTypes.object.isRequired,
  routeParams: PropTypes.object.isRequired
};

function mapStateToProps(state, ownProps) {
  const {
    content: partner,
    loaded: partnerLoaded,
    loading: partnerLoading
  } = getPartner(state, ownProps.routeParams.partnerId);
  const partnerTypes = getPartnerTypes(state);

  const currencies = getCurrencies(state);
  const bankAccountTypes = getBankAccountTypes(state);
  const bankList = getBanks(state);
  const personDocumentTypes = getPersonDocumentTypes(state);

  return {
    partner,
    partnerLoaded,
    partnerTypes,
    currencies,
    bankAccountTypes,
    bankList,
    partnerLoading,
    personDocumentTypes
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadPartner,
        loadPartnerTypes,
        loadCounties,
        loadCities,
        loadClientByIdCard,
        loadPersonDocumentTypes,
        getPartnerNationalityId: getPartnerNationalityId
      },
      dispatch
    ),
    dispatch
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withParams(Container)));
